import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  X,
} from 'lucide-react';
import { useDarshanStatusWs } from '../../hooks/useDarshanStatusWs';

function formatTime(isoStr?: string | null): string {
  if (!isoStr) return '';
  const match = isoStr.match(/T(\d{2}:\d{2})/);
  if (match) return `${match[1]} IST`;
  return isoStr;
}

export const DarshanAlertBanner: React.FC = () => {
  const { status, isConnected } = useDarshanStatusWs();
  const [isDismissed, setIsDismissed] = useState<boolean>(false);

  useEffect(() => {
    const isVisible = Boolean(status && !isDismissed);
    document.documentElement.style.setProperty('--topbar-total-h', isVisible ? '92px' : '56px');
    return () => {
      document.documentElement.style.setProperty('--topbar-total-h', '56px');
    };
  }, [status, isDismissed]);

  if (!status || isDismissed) {
    return null;
  }

  const isStopped = status.stopped || status.state === 'STOPPED';
  // entries paused: the doors quiet for 3+ min while the 10-minute rule has not called it a stop yet (15 Oct 2026:
  // darshan stopped 05:53 and the banner said "in progress" until ~06:04)
  const quiet = Number((status as { quiet_minutes?: number }).quiet_minutes ?? 0);
  const isPaused = !isStopped && status.state === 'RUNNING' && quiet >= 3;
  const tone = isStopped ? 'stop' : isPaused ? 'pause' : 'run';
  const T = {
    stop: { strip: 'bg-[#FDE7EA] dark:bg-[#2a1418] border-b-2 border-[#BE123C] dark:border-[#F43F5E]/60 text-[#7F1D1D] dark:text-red-100',
      tag: 'bg-white dark:bg-[#F43F5E]/15 border-[#BE123C] dark:border-[#F43F5E]/60 text-[#BE123C] dark:text-[#FDA4AF]',
      text: 'text-[#7F1D1D] font-bold dark:text-red-100', dot: 'text-[#BE123C] dark:text-[#FDA4AF] font-semibold' },
    pause: { strip: 'bg-[#FEF3C7] dark:bg-[#2a2210] border-b-2 border-amber-500 dark:border-amber-400/60 text-[#78350F] dark:text-amber-100',
      tag: 'bg-white dark:bg-amber-400/15 border-amber-500 dark:border-amber-400/60 text-amber-800 dark:text-amber-300',
      text: 'text-[#78350F] font-bold dark:text-amber-100', dot: 'text-amber-600 dark:text-amber-300 font-semibold' },
    run: { strip: 'bg-[#E3F4EA] dark:bg-[#181a22] border-b-2 border-emerald-600/70 dark:border-emerald-500/25 text-[#14532D] dark:text-neutral-200',
      tag: 'bg-white dark:bg-emerald-500/15 border-emerald-600 dark:border-emerald-500/40 text-emerald-800 dark:text-emerald-400',
      text: 'text-[#14532D] font-bold dark:text-neutral-200', dot: 'text-emerald-700 dark:text-emerald-400 font-semibold' },
  }[tone];

  const bannerText = isStopped
    ? `Darshan Suspended  ·  Stopped since: ${formatTime(status.since) || '—'} (${status.minutes}m)  ·  Entries last 5m: ${status.in_last_5_min}  ·  Exits last 5m: ${status.out_last_5_min}`
    : isPaused
    ? `Darshan entries paused  ·  Almost nobody through the darshan doors for ${quiet} min  ·  Marked suspended after 10 min  ·  Entries last 5m: ${status.in_last_5_min}  ·  Exits last 5m: ${status.out_last_5_min}`
    : `Darshan in Progress  ·  Entries last 5m: ${status.in_last_5_min}  ·  Exits last 5m: ${status.out_last_5_min}`;

  return (
    <div
      role="alert"
      className={`relative w-full z-50 overflow-hidden transition-colors duration-200 ${T.strip}`}
    >
      <div className="flex items-center h-9 px-3 text-sm">
        {/* Simple & Clean Left Status Tag */}
        <div
          className={`flex-shrink-0 flex items-center gap-1.5 px-2.5 py-0.5 rounded text-[13px] font-extrabold tracking-wide mr-3 border transition-colors ${T.tag}`}
        >
          {isStopped ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-[#F43F5E]" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#BE123C]" />
              </span>
              <AlertTriangle className="w-4 h-4 text-[#BE123C] dark:text-[#FDA4AF] shrink-0" />
              <span>Darshan Suspended</span>
              {status.minutes > 0 && (
                <span className="ml-0.5 px-1 py-0.2 rounded bg-[#BE123C]/15 dark:bg-[#F43F5E]/25 text-[#9F1239] dark:text-red-100 text-[11.5px]">
                  {status.minutes}m
                </span>
              )}
            </>
          ) : isPaused ? (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-amber-400" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
              </span>
              <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-300 shrink-0" />
              <span>Entries paused</span>
              <span className="ml-0.5 px-1 py-0.2 rounded bg-amber-500/15 text-amber-900 dark:text-amber-100 text-[11.5px]">{quiet}m</span>
            </>
          ) : (
            <>
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 bg-emerald-400" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span>Darshan in Progress</span>
            </>
          )}

          {/* Connection dot */}
          <span
            title={isConnected ? 'Connected to live Darshan WebSocket' : 'Connecting to Darshan WebSocket...'}
            className={`w-1.5 h-1.5 rounded-full ml-0.5 ${isConnected ? 'bg-emerald-500 dark:bg-emerald-400' : 'bg-amber-500 dark:bg-amber-400 animate-pulse'}`}
          />
        </div>

        {/* Clean Moving Marquee Text */}
        <div className="relative flex-1 overflow-hidden h-full flex items-center group cursor-default">
          <div className="darshan-ticker flex items-center whitespace-nowrap gap-16 group-hover:[animation-play-state:paused]">
            <span className={`inline-flex items-center gap-2 text-[15px] tracking-normal ${T.text}`}>
              <span className={T.dot}>●</span>
              {bannerText}
            </span>
            <span className={`inline-flex items-center gap-2 text-[15px] tracking-normal ${T.text}`}>
              <span className={T.dot}>●</span>
              {bannerText}
            </span>
          </div>
        </div>

        {/* Right Subtle Action Icons */}
        <div className="flex-shrink-0 flex items-center gap-1 ml-3 text-neutral-600 dark:text-neutral-400">
          <button
            onClick={() => setIsDismissed(true)}
            title="Dismiss banner"
            className="p-1 rounded hover:bg-neutral-200/80 dark:hover:bg-neutral-800 text-neutral-700 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-200 transition-colors cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes darshanScroll {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .darshan-ticker {
          display: inline-flex;
          animation: darshanScroll 26s linear infinite;
        }
      `}</style>
    </div>
  );
};
